import { useEffect, useState } from 'react';

import SectionHeader from '@/components/shared/section-header';
import { MasonryGrid, MasonryGridItem } from '@/components/ui/masonry-grid';
import { Separator } from '@/components/ui/separator';
import { MutedText } from '@/components/ui/typography';
import http from '@/lib/http';
import { cmsPageTemplatesPath } from '@/lib/routes';
import type { SectionStructure } from '@/types/page';
import type { SectionTemplateItem } from '@/types/section-template';
import { INDEX_PAGE_TYPES, PAGE_TEMPLATES, type SidebarSelection } from './page-template-types';
import { type PageTemplateFromApi, TemplatePreviewCard } from './template-preview-card';

const PAGE_TYPES = [...PAGE_TEMPLATES, ...INDEX_PAGE_TYPES];

interface TemplateListProps {
  isLoadingTemplates: boolean;
  isLoadingSectionTemplates: boolean;
  pageTemplates: PageTemplateFromApi[];
  templatesByCode: Map<string, SectionTemplateItem>;
  isCreating: boolean;
  onSelectTemplate: (
    pageType: string,
    templateCode?: string,
    sectionsStructure?: SectionStructure[],
  ) => void;
  pageType: string;
  title?: string;
  description?: string;
}

function TemplateList({
  isLoadingTemplates,
  isLoadingSectionTemplates,
  pageTemplates,
  templatesByCode,
  isCreating,
  onSelectTemplate,
  pageType,
  title,
  description,
}: TemplateListProps) {
  return (
    <>
      {title && (
        <div className="mb-6 space-y-1">
          <SectionHeader title={title} description={description} />
          <Separator className="mt-2 mb-4" />
        </div>
      )}
      {isLoadingTemplates || isLoadingSectionTemplates ? (
        <div className="flex h-full items-center justify-center">
          <MutedText>Loading templates...</MutedText>
        </div>
      ) : pageTemplates.length === 0 ? (
        <div className="flex h-full items-center justify-center">
          <MutedText>No templates available for this page type.</MutedText>
        </div>
      ) : (
        <MasonryGrid columns={2} gap="md">
          {pageTemplates.map((template) => (
            <MasonryGridItem key={template.id}>
              <TemplatePreviewCard
                template={template}
                templatesByCode={templatesByCode}
                onClick={() =>
                  onSelectTemplate(pageType, template.code, template.sectionsStructure)
                }
                disabled={isCreating || isLoadingSectionTemplates}
              />
            </MasonryGridItem>
          ))}
        </MasonryGrid>
      )}
    </>
  );
}

interface AddPageContentProps {
  selectedItem: SidebarSelection;
  templatesByCode: Map<string, SectionTemplateItem>;
  isLoadingSectionTemplates: boolean;
  onAddTemplate: (
    pageType: string,
    templateCode?: string,
    sectionsStructure?: SectionStructure[],
  ) => void;
  isCreating?: boolean;
  childPageType?: string;
}

export function AddPageContent({
  selectedItem,
  templatesByCode,
  isLoadingSectionTemplates,
  onAddTemplate,
  isCreating = false,
  childPageType,
}: AddPageContentProps) {
  const [templateResult, setTemplateResult] = useState<{
    pageType: string;
    templates: PageTemplateFromApi[];
  } | null>(null);

  const selectedData = PAGE_TYPES.find((option) => option.key === selectedItem);

  // Derive pageType from childPageType or selectedData
  const pageType = childPageType || selectedData?.key || null;

  useEffect(() => {
    let cancelled = false;
    if (!pageType) {
      setTemplateResult(null);
      return;
    }

    setTemplateResult(null);

    http
      .get<{ data: PageTemplateFromApi[] }>(cmsPageTemplatesPath(), {
        params: { pageType },
      })
      .then((response) => {
        if (!cancelled) setTemplateResult({ pageType, templates: response.data.data });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to fetch page templates:', error);
        setTemplateResult({ pageType, templates: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [pageType]);

  if (pageType) {
    const commonProps = {
      isLoadingTemplates: templateResult?.pageType !== pageType,
      isLoadingSectionTemplates,
      pageTemplates: templateResult?.pageType === pageType ? templateResult.templates : [],
      templatesByCode,
      isCreating,
      onSelectTemplate: onAddTemplate,
      pageType,
      ...(childPageType
        ? {}
        : {
            title: selectedData?.label,
            description: selectedData?.description,
          }),
    };

    return <TemplateList {...commonProps} />;
  }

  // Default state - no selection
  return (
    <div className="flex h-full items-center justify-center">
      <MutedText>Select an option from the sidebar to see details.</MutedText>
    </div>
  );
}
